import { DEFAULT_MAX_ORG_LEVEL } from "./orgConstants";
import { getOrgCustomers } from "./customerModel";

/** Synthetic tree id prefix for suppliers nested under an org (never a real organization id). */
export const SUPPLIER_TREE_ID_PREFIX = "__sup__";

export function buildSupplierTreeId(orgId, customerKey) {
  return `${SUPPLIER_TREE_ID_PREFIX}${orgId}:${customerKey}`;
}

/**
 * @param {string} nodeId
 * @returns {{ orgId: string, key: string } | null}
 */
export function parseSupplierTreeNodeId(nodeId) {
  if (typeof nodeId !== "string" || !nodeId.startsWith(SUPPLIER_TREE_ID_PREFIX)) return null;
  const rest = nodeId.slice(SUPPLIER_TREE_ID_PREFIX.length);
  const i = rest.indexOf(":");
  if (i === -1) return null;
  return { orgId: rest.slice(0, i), key: rest.slice(i + 1) };
}

/** @param {unknown} node */
export function isSupplierTreeNode(node) {
  return Boolean(node && typeof node === "object" && /** @type {{ _treeKind?: string }} */ (node)._treeKind === "supplier");
}

function appendSuppliersUnderOrgs(nodes, flatById) {
  return nodes.map((node) => {
    const orgChildren = appendSuppliersUnderOrgs(node.children || [], flatById);
    const row = flatById[node.id];
    /** @type {typeof orgChildren} */
    const supplierNodes = [];
    if (row) {
      const list = getOrgCustomers(row);
      const sorted = [...list].sort((a, b) =>
        String(a.customerCode || "").localeCompare(String(b.customerCode || ""), undefined, { sensitivity: "base" })
      );
      sorted.forEach((sup, idx) => {
        const key = String(sup.customerId || sup.customerCode || idx);
        const code = String(sup.customerCode || "—").trim() || "—";
        const name = String(sup.customerName || "—").trim() || "—";
        supplierNodes.push({
          id: buildSupplierTreeId(node.id, key),
          code,
          name,
          type: "Supplier",
          parentId: node.id,
          status: String(sup.customerStatus || "—"),
          children: [],
          _treeKind: "supplier",
          _parentOrgId: node.id,
          _supplier: sup,
        });
      });
    }
    return { ...node, children: [...orgChildren, ...supplierNodes] };
  });
}

function extendLevelsAndPathsForSuppliers(nodes, baseLevels, basePaths) {
  const levels = { ...baseLevels };
  const paths = { ...basePaths };
  const walk = (arr) => {
    for (const n of arr) {
      if (isSupplierTreeNode(n) && n._parentOrgId) {
        const parentLevel = baseLevels[n._parentOrgId] ?? 1;
        levels[n.id] = parentLevel + 1;
        const pPath = basePaths[n._parentOrgId] || "";
        paths[n.id] = pPath ? `${pPath} / ${n.code} · ${n.name}` : `${n.code} · ${n.name}`;
      }
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return { levels, paths };
}

/**
 * Organization tree plus supplier rows nested under their organization (display-only hierarchy).
 * @param {OrgFlatNode[]} flat
 */
export function buildTreeWithMetaAndSuppliers(flat) {
  const { tree, levels, paths } = buildTreeWithMeta(flat);
  const flatById = Object.fromEntries(flat.map((r) => [r.id, r]));
  const treeWithSuppliers = appendSuppliersUnderOrgs(tree, flatById);
  const { levels: levelsOut, paths: pathsOut } = extendLevelsAndPathsForSuppliers(treeWithSuppliers, levels, paths);
  return { tree: treeWithSuppliers, levels: levelsOut, paths: pathsOut };
}

/**
 * @param {OrgFlatNode[]} flat
 * @param {string} nodeId
 * @returns {{ org: OrgFlatNode, supplier: Record<string, unknown> } | null}
 */
export function resolveSupplierTreeSelection(flat, nodeId) {
  const parsed = parseSupplierTreeNodeId(nodeId);
  if (!parsed) return null;
  const org = flat.find((o) => o.id === parsed.orgId);
  if (!org) return null;
  const list = getOrgCustomers(org);
  const sup = list.find((c, i) => String(c.customerId || c.customerCode || i) === parsed.key);
  return sup ? { org, supplier: sup } : null;
}

/**
 * @typedef {Object} OrgContactRow
 * @property {string} id
 * @property {string} [name]
 * @property {string} [email]
 * @property {string} [mobile]
 * @property {string} [role]
 */

/**
 * @typedef {Object} OrgFlatNode
 * @property {string} id
 * @property {string} code
 * @property {string} name
 * @property {string} [shortName]
 * @property {string} type
 * @property {string|null} parentId
 * @property {string} status
 * @property {number} [hierarchyLevel]
 * @property {string} [hierarchyPath]
 * @property {string} [tenantId]
 * @property {string} [effectiveFrom]
 * @property {string} [effectiveTo]
 * @property {boolean} [legalEntity]
 * @property {boolean} [operationalEntity]
 * @property {string} [currency]
 * @property {string} [country]
 * @property {string} [state]
 * @property {string} [city]
 * @property {string} [address1]
 * @property {string} [address2]
 * @property {string} [postalCode]
 * @property {string} [timeZone]
 * @property {string} [primaryContact]
 * @property {string} [primaryEmail]
 * @property {string} [primaryMobile]
 * @property {OrgContactRow[]} [contacts]
 * @property {string} [createdBy]
 * @property {string} [createdOn]
 * @property {string} [updatedBy]
 * @property {string} [updatedOn]
 * @property {string[]} [mappedUserIds]
 * @property {string[]} [mappedCustomerIds]
 * @property {Record<string, unknown>} [extra]
 */

/**
 * @param {OrgFlatNode[]} flat
 * @returns {{ tree: OrgFlatNode[], levels: Record<string, number>, paths: Record<string, string> }}
 */
export function buildTreeWithMeta(flat) {
  const byId = Object.fromEntries(
    flat.map((n) => [n.id, { ...n, children: /** @type {OrgFlatNode[]} */ ([]) }])
  );
  const roots = [];
  for (const n of flat) {
    const node = byId[n.id];
    if (!n.parentId) roots.push(node);
    else if (byId[n.parentId]) byId[n.parentId].children.push(node);
  }
  const sortRec = (nodes) => {
    nodes.sort((a, b) => String(a.code).localeCompare(String(b.code)));
    nodes.forEach((x) => sortRec(x.children || []));
  };
  sortRec(roots);

  const levels = {};
  const paths = {};

  const walk = (node, depth, chain) => {
    levels[node.id] = depth;
    const nextChain = [...chain, node.name];
    paths[node.id] = nextChain.join(" / ");
    (node.children || []).forEach((ch) => walk(ch, depth + 1, nextChain));
  };
  roots.forEach((r) => walk(r, 1, []));

  return { tree: roots, levels, paths };
}

/**
 * True if `candidateId` is `rootId` or any descendant under `rootId`.
 * @param {OrgFlatNode[]} flat
 * @param {string} rootId
 * @param {string} candidateId
 */
export function isUnderSubtree(flat, rootId, candidateId) {
  if (candidateId === rootId) return true;
  const kids = flat.filter((x) => x.parentId === rootId);
  return kids.some((k) => isUnderSubtree(flat, k.id, candidateId));
}

/**
 * @param {OrgFlatNode[]} flat
 * @param {string} dragId
 * @param {string|null} newParentId
 * @param {number} [maxLevel]
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateReparent(flat, dragId, newParentId, maxLevel = DEFAULT_MAX_ORG_LEVEL) {
  if (newParentId === dragId) {
    return { ok: false, reason: "Cannot assign an organization as its own parent." };
  }
  if (newParentId && isUnderSubtree(flat, dragId, newParentId)) {
    return { ok: false, reason: "Invalid move: would create a circular hierarchy." };
  }

  const next = flat.map((n) => (n.id === dragId ? { ...n, parentId: newParentId } : n));
  const { levels } = buildTreeWithMeta(next);
  const maxDepth = Math.max(0, ...Object.values(levels));
  if (maxDepth > maxLevel) {
    return {
      ok: false,
      reason: `Move exceeds the configured maximum hierarchy depth (${maxLevel} levels).`,
    };
  }

  const dragged = next.find((x) => x.id === dragId);
  const parent = newParentId ? next.find((x) => x.id === newParentId) : null;
  if (parent && dragged && parent.status !== "Active" && dragged.status === "Active") {
    return {
      ok: false,
      reason: "An active organization cannot be placed under an inactive parent.",
    };
  }

  return { ok: true };
}

/**
 * Apply parent change and return updated flat list (immutable).
 * @param {OrgFlatNode[]} flat
 * @param {string} dragId
 * @param {string|null} newParentId
 */
export function applyReparent(flat, dragId, newParentId) {
  return flat.map((n) => (n.id === dragId ? { ...n, parentId: newParentId } : n));
}

/**
 * Collect all node ids in tree (DFS).
 * @param {OrgFlatNode[]} nodes
 */
export function collectIds(nodes) {
  const ids = [];
  const walk = (arr) => {
    for (const n of arr) {
      ids.push(n.id);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return ids;
}

/**
 * Filter tree for search: keep branches that match name/code or have matching descendants.
 * @param {OrgFlatNode[]} nodes
 * @param {string} query
 */
export function filterTree(nodes, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return nodes;
  const filterNode = (node) => {
    const selfMatch =
      String(node.name).toLowerCase().includes(q) || String(node.code).toLowerCase().includes(q);
    const kids = (node.children || []).map(filterNode).filter(Boolean);
    if (selfMatch || kids.length) {
      return { ...node, children: kids };
    }
    return null;
  };
  return nodes.map(filterNode).filter(Boolean);
}
